import { useEffect, useState, useRef } from "react";
import api, { formatApiError, getSignedFileUrl } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { Plus, Trash2, TrendingUp, TrendingDown, Euro, Download, Printer, Camera, Receipt, Loader2, Wallet, EyeOff, Home, Pencil } from "lucide-react";
import { generateInvoicePdf } from "../lib/pdf";
import InvoiceIntake from "../components/InvoiceIntake";
import { useNavigate } from "react-router-dom";
import AccountsPanel from "../components/accounting/AccountsPanel";
import CashClosingPanel from "../components/accounting/CashClosingPanel";
import VatPanel from "../components/accounting/VatPanel";
import PendingDocsPanel from "../components/accounting/PendingDocsPanel";

const today = new Date().toISOString().slice(0, 10);
const fmt = (n) => `€ ${(n || 0).toFixed(2)}`;
const emptyTx = { kind: "incasso", ledger: "ufficiale", category: "", amount: "", description: "", date: today, payment_method: "contante", tax_rate: 10 };
const emptyInv = { number: "", client: "", subtotal: "", tax_rate: 10, date: today, status: "emessa" };
const EXPENSE_CATS = ["Fornitori", "Materie prime", "Utenze", "Affitto", "Personale", "Manutenzione", "Attrezzature", "Marketing", "Tasse e imposte", "Trasporti", "Bar / Beverage", "Spese familiari", "Altro"];
const INCOME_CATS = ["Prelievo cassa", "Bonifico", "SumUp"];
const INCOME_METHOD = { "Prelievo cassa": "contante", "Bonifico": "banca", "SumUp": "sumup" };
const WALLETS = [{ v: "contante", l: "Contanti" }, { v: "sumup", l: "SumUp" }, { v: "banca", l: "Banca" }, { v: "deposito", l: "Deposito" }];

const ledgerOf = (t) => t.ledger || "ufficiale";
// Etichetta discreta di ogni movimento in tabella
const txLabel = (t) => {
  const l = ledgerOf(t);
  if (t.kind === "incasso") return l === "black" ? "Preventivo" : "Incasso";
  if (l === "black") return "Acquisto black";
  if (l === "fuori_cassa") return "Fuori cassa";
  return "Spesa";
};
const ledgerBadge = (t) => {
  const l = ledgerOf(t);
  if (l === "black") return { text: "Black", cls: "bg-amber-500/15 text-amber-600 dark:text-amber-400" };
  if (l === "fuori_cassa") return { text: "Fuori cassa", cls: "bg-violet-500/15 text-violet-600 dark:text-violet-400" };
  return { text: "Ufficiale", cls: "bg-sky-500/15 text-sky-600 dark:text-sky-400" };
};

export default function Accounting() {
  const navigate = useNavigate();
  const reg = (t) => ({ role: "link", tabIndex: 0, className: "p-5 rounded-xl cursor-pointer hover:border-primary/60 hover:shadow-md transition-[box-shadow,border-color]", onClick: () => navigate(`/contabilita/registro/${t}`), title: "Apri il registro movimenti" });
  const [txns, setTxns] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [txForm, setTxForm] = useState(emptyTx);
  const [invForm, setInvForm] = useState(emptyInv);
  const [txOpen, setTxOpen] = useState(false);
  const [invOpen, setInvOpen] = useState(false);
  const [txFile, setTxFile] = useState(null);
  const [txReading, setTxReading] = useState(false);
  const txFileRef = useRef(null);
  const [suppliers, setSuppliers] = useState([]);
  const [debtOpen, setDebtOpen] = useState(false);
  const [debtForm, setDebtForm] = useState({ name: "", amount: "", note: "" });
  const [ledgerFilter, setLedgerFilter] = useState("tutti");
  const [pendingCount, setPendingCount] = useState(0);
  const [accounts, setAccounts] = useState([]);
  const [editTxId, setEditTxId] = useState(null);
  const [invEditId, setInvEditId] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [paySup, setPaySup] = useState(null);
  const [payForm, setPayForm] = useState({ amount: "", payment_method: "contante" });

  const onTxFile = async (f) => {
    setTxFile(f);
    if (!f) return;
    setTxReading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/ai/extract-receipt", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setTxForm((t) => ({ ...t, kind: "spesa", amount: data.amount ? String(data.amount) : t.amount, category: data.category || t.category }));
      toast.success("Scontrino letto dall'AI");
    } catch { toast.error("Impossibile leggere lo scontrino"); }
    finally { setTxReading(false); }
  };

  const viewReceipt = async (path) => {
    try {
      const url = await getSignedFileUrl(path);
      window.open(url, "_blank");
    } catch { toast.error("Impossibile aprire il documento"); }
  };

  const load = async () => {
    const [t, i, s, d, a] = await Promise.all([api.get("/transactions"), api.get("/invoices"), api.get("/suppliers").catch(() => ({ data: [] })), api.get("/documents?status=pending").catch(() => ({ data: [] })), api.get("/accounts").catch(() => ({ data: [] }))]);
    setTxns(t.data); setInvoices(i.data); setSuppliers(s.data); setPendingCount((d.data || []).length); setAccounts(a.data || []);
  };
  useEffect(() => { load(); }, []);

  const debtors = suppliers.filter((s) => (s.debt || 0) > 0);
  const totalDebt = suppliers.reduce((a, s) => a + (s.debt || 0), 0);
  const addDebt = async () => {
    if (!debtForm.name || !debtForm.amount) return toast.error("Fornitore e importo obbligatori");
    try { await api.post("/suppliers/debt", { name: debtForm.name, amount: parseFloat(debtForm.amount), note: debtForm.note }); toast.success("Debito registrato"); setDebtOpen(false); setDebtForm({ name: "", amount: "", note: "" }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const payDebt = async (s) => {
    setPaySup(s); setPayForm({ amount: String(s.debt || 0), payment_method: "contante" }); setPayOpen(true);
  };
  const doPay = async () => {
    const amt = parseFloat(payForm.amount);
    if (!amt || amt <= 0) return toast.error("Importo non valido");
    try { await api.post(`/suppliers/${paySup.id}/pay`, { amount: amt, payment_method: payForm.payment_method }); toast.success("Pagamento registrato"); setPayOpen(false); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Riepilogo per registro
  const sumBy = (pred) => txns.filter(pred).reduce((s, t) => s + (t.amount || 0), 0);
  const incassoUff = sumBy((t) => t.kind === "incasso" && ledgerOf(t) === "ufficiale");
  const ivaUff = txns.filter((t) => t.kind === "incasso" && ledgerOf(t) === "ufficiale").reduce((s, t) => s + (t.tax || 0), 0);
  const preventivi = sumBy((t) => t.kind === "incasso" && ledgerOf(t) === "black");
  const speseUff = sumBy((t) => t.kind === "spesa" && ledgerOf(t) === "ufficiale");
  const acquistiBlack = sumBy((t) => t.kind === "spesa" && ledgerOf(t) === "black");
  const fuoriCassa = sumBy((t) => t.kind === "spesa" && ledgerOf(t) === "fuori_cassa");
  const utileAttivita = incassoUff + preventivi - speseUff - acquistiBlack;

  const filteredTxns = txns.filter((t) => ledgerFilter === "tutti" ? true : ledgerOf(t) === ledgerFilter);

  const saveTx = async () => {
    if (!txForm.amount) return toast.error("Importo obbligatorio");
    let category = txForm.category;
    if (!category) {
      if (txForm.ledger === "black" && txForm.kind === "incasso") category = "Preventivi";
      else if (txForm.ledger === "fuori_cassa") category = "Spese familiari";
    }
    if (!category) return toast.error("Categoria obbligatoria");
    const isUffInc = txForm.ledger === "ufficiale" && (txForm.kind === "incasso" || txForm.kind === "spesa");
    const payload = { ...txForm, category, amount: parseFloat(txForm.amount), tax_rate: isUffInc ? parseFloat(txForm.tax_rate) || 0 : 0 };
    try {
      if (editTxId) await api.put(`/transactions/${editTxId}`, payload);
      else await api.post("/transactions", payload);
      toast.success(editTxId ? "Movimento aggiornato" : "Movimento registrato"); setTxOpen(false); setEditTxId(null); setTxForm(emptyTx); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openEditTx = (t) => {
    setEditTxId(t.id);
    setTxForm({ kind: t.kind, ledger: ledgerOf(t), category: t.category || "", amount: String(t.amount ?? ""), description: t.description || "", date: t.date || today, payment_method: t.payment_method || "contante", tax_rate: t.tax_rate || 0 });
    setTxOpen(true);
  };
  const saveInv = async () => {
    if (!invForm.number || !invForm.subtotal) return toast.error("Numero e imponibile obbligatori");
    const sub = parseFloat(invForm.subtotal), rate = parseFloat(invForm.tax_rate);
    const tax = +(sub * rate / 100).toFixed(2);
    const payload = { ...invForm, subtotal: sub, tax_rate: rate, tax, total: +(sub + tax).toFixed(2), items: [] };
    try {
      if (invEditId) await api.put(`/invoices/${invEditId}`, payload);
      else await api.post("/invoices", payload);
      toast.success(invEditId ? "Fattura aggiornata" : "Fattura creata"); setInvOpen(false); setInvEditId(null); setInvForm(emptyInv); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openEditInv = (v) => {
    setInvEditId(v.id);
    setInvForm({ number: v.number || "", client: v.client || "", subtotal: String(v.subtotal ?? ""), tax_rate: v.tax_rate ?? 10, date: v.date || today, status: v.status || "emessa" });
    setInvOpen(true);
  };
  const delTx = async (id) => { await api.delete(`/transactions/${id}`); load(); };
  const delInv = async (id) => { await api.delete(`/invoices/${id}`); load(); };

  const openNewTx = (ledger) => { setEditTxId(null); setTxForm({ ...emptyTx, ledger, kind: ledger === "ufficiale" ? "incasso" : "spesa" }); setTxOpen(true); };
  const showIva = txForm.ledger === "ufficiale";
  const grossPrev = parseFloat(txForm.amount) || 0;
  const ratePrev = parseFloat(txForm.tax_rate) || 0;
  const imponibilePrev = ratePrev > 0 ? grossPrev / (1 + ratePrev / 100) : grossPrev;

  return (
    <div>
      <PageHeader title="Contabilità" subtitle="Incassi ufficiali, preventivi, acquisti e spese fuori cassa" testid="accounting-header">
        <div className="flex gap-2">
          <InvoiceIntake onDone={load} docType="fattura" />
          <InvoiceIntake onDone={load} docType="scontrino" />
        </div>
      </PageHeader>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <Card className="p-5 rounded-xl border-sky-500/30" data-testid="acc-total-ufficiale"><div className="flex justify-between"><span className="text-xs uppercase text-muted-foreground">Contabilità Ufficiale</span><Euro className="w-4 h-4 text-sky-500" /></div><p className="font-dash text-3xl font-semibold mt-2">{fmt(incassoUff - speseUff)}</p><p className="text-xs text-muted-foreground mt-1">incassi ufficiali − spese ufficiali · IVA {fmt(ivaUff)}</p></Card>
        <Card className="p-5 rounded-xl border-amber-500/30" data-testid="acc-total-preventivi"><div className="flex justify-between"><span className="text-xs uppercase text-muted-foreground">Contabilità da Preventivi</span><EyeOff className="w-4 h-4 text-amber-500" /></div><p className="font-dash text-3xl font-semibold mt-2">{fmt(preventivi - acquistiBlack)}</p><p className="text-xs text-muted-foreground mt-1">solo incassi e acquisti black · l'ufficiale è escluso</p></Card>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
        <Card {...reg("incassi")} data-testid="acc-income-official"><div className="flex justify-between"><span className="text-xs uppercase text-muted-foreground">Incassi ufficiali</span><TrendingUp className="w-4 h-4 text-sky-500" /></div><p className="font-dash text-2xl font-semibold mt-3">{fmt(incassoUff)}</p><p className="text-xs text-muted-foreground mt-1">di cui IVA {fmt(ivaUff)} · <span className="underline">apri registro</span></p></Card>
        <Card {...reg("preventivi")} data-testid="acc-preventivi"><div className="flex justify-between"><span className="text-xs uppercase text-muted-foreground">Preventivi</span><EyeOff className="w-4 h-4 text-amber-500" /></div><p className="font-dash text-2xl font-semibold mt-3">{fmt(preventivi)}</p><p className="text-xs text-muted-foreground mt-1">incassi black · <span className="underline">apri registro</span></p></Card>
        <Card className="p-5 rounded-xl" data-testid="acc-profit"><div className="flex justify-between"><span className="text-xs uppercase text-muted-foreground">Utile attività</span><Euro className="w-4 h-4 text-primary" /></div><p className="font-dash text-2xl font-semibold mt-3">{fmt(utileAttivita)}</p></Card>
      </div>
      <div className="grid grid-cols-3 gap-4 mb-4">
        <Card {...reg("spese")} className="p-4 rounded-xl cursor-pointer hover:border-primary/60 hover:shadow-md transition-[box-shadow,border-color]" data-testid="acc-expense-official"><div className="flex justify-between"><span className="text-xs uppercase text-muted-foreground">Spese ufficiali</span><TrendingDown className="w-4 h-4 text-destructive" /></div><p className="font-dash text-xl font-semibold mt-2">{fmt(speseUff)}</p><p className="text-xs text-muted-foreground mt-1 underline">apri registro</p></Card>
        <Card {...reg("black")} className="p-4 rounded-xl cursor-pointer hover:border-primary/60 hover:shadow-md transition-[box-shadow,border-color]" data-testid="acc-purchases-black"><div className="flex justify-between"><span className="text-xs uppercase text-muted-foreground">Acquisti black</span><Wallet className="w-4 h-4 text-amber-500" /></div><p className="font-dash text-xl font-semibold mt-2">{fmt(acquistiBlack)}</p><p className="text-xs text-muted-foreground mt-1 underline">apri registro</p></Card>
        <Card {...reg("fuori_cassa")} className="p-4 rounded-xl cursor-pointer hover:border-primary/60 hover:shadow-md transition-[box-shadow,border-color]" data-testid="acc-out-of-cash"><div className="flex justify-between"><span className="text-xs uppercase text-muted-foreground">Fuori cassa</span><Home className="w-4 h-4 text-violet-500" /></div><p className="font-dash text-xl font-semibold mt-2">{fmt(fuoriCassa)}</p><p className="text-xs text-muted-foreground mt-1">spese familiari · <span className="underline">apri registro</span></p></Card>
      </div>
      <div className="flex flex-wrap gap-2 mb-8" data-testid="acc-register-links">
        <span className="text-xs uppercase text-muted-foreground self-center mr-1">Registri:</span>
        {[["sumup", "SumUp"], ["cassa", "Cassa EasyEvolver"], ["fatture", "Fatture sospesi"], ["incassi", "Incassi ufficiali"], ["spese", "Spese ufficiali"], ["preventivi", "Preventivi"], ["black", "Acquisti black"], ["fuori_cassa", "Fuori cassa"]].map(([t, l]) => (
          <Button key={t} size="sm" variant="outline" className="rounded-full" onClick={() => navigate(`/contabilita/registro/${t}`)} data-testid={`acc-register-${t}`}>{l}</Button>
        ))}
      </div>

      <Card className="p-5 rounded-xl mb-8" data-testid="acc-accounts-summary">
        <div className="flex items-center justify-between mb-3"><span className="text-xs uppercase text-muted-foreground flex items-center gap-1.5"><Wallet className="w-4 h-4 text-primary" /> Conti attuali</span><span className="text-sm font-medium" data-testid="acc-accounts-total">Totale {fmt(accounts.reduce((s, a) => s + (a.balance || 0), 0))}</span></div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {accounts.map((a) => (
            <div key={a.key} className="rounded-xl border border-border/60 p-3" data-testid={`acc-balance-${a.key}`}>
              <p className="text-xs text-muted-foreground">{a.name}</p>
              <p className="font-dash text-xl font-semibold mt-1">{fmt(a.balance)}</p>
            </div>
          ))}
        </div>
      </Card>

      <Tabs defaultValue="movimenti">
        <TabsList data-testid="accounting-tabs">
          <TabsTrigger value="movimenti" data-testid="tab-movimenti">Movimenti</TabsTrigger>
          <TabsTrigger value="fatture" data-testid="tab-fatture">Fatture / Scontrini</TabsTrigger>
          <TabsTrigger value="debiti" data-testid="tab-debiti">Debiti Fornitori</TabsTrigger>
          <TabsTrigger value="conti" data-testid="tab-conti">Conti / Cassa</TabsTrigger>
          <TabsTrigger value="chiusura" data-testid="tab-chiusura">Chiusura cassa</TabsTrigger>
          <TabsTrigger value="iva" data-testid="tab-iva">IVA</TabsTrigger>
          <TabsTrigger value="dasistemare" data-testid="tab-dasistemare">Da sistemare{pendingCount > 0 && <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-semibold rounded-full bg-destructive text-destructive-foreground" data-testid="pending-badge">{pendingCount}</span>}</TabsTrigger>
        </TabsList>

        <TabsContent value="movimenti">
          <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
            <div className="w-48">
              <Select value={ledgerFilter} onValueChange={setLedgerFilter}>
                <SelectTrigger data-testid="ledger-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="tutti">Tutti i registri</SelectItem>
                  <SelectItem value="ufficiale">Ufficiale</SelectItem>
                  <SelectItem value="black">Black / Preventivi</SelectItem>
                  <SelectItem value="fuori_cassa">Fuori cassa (familiari)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button onClick={() => openNewTx("ufficiale")} className="rounded-full" data-testid="add-transaction-button"><Plus className="w-4 h-4 mr-1" /> Nuovo movimento</Button>
          </div>
          <Card className="rounded-xl overflow-hidden">
            <Table data-testid="transactions-table">
              <TableHeader><TableRow><TableHead>Data</TableHead><TableHead>Registro</TableHead><TableHead>Tipo</TableHead><TableHead>Categoria</TableHead><TableHead>Descrizione</TableHead><TableHead>Metodo</TableHead><TableHead>Scontrino</TableHead><TableHead className="text-right">Importo</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {filteredTxns.length === 0 && <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-8">Nessun movimento</TableCell></TableRow>}
                {filteredTxns.map((t) => {
                  const b = ledgerBadge(t);
                  return (
                  <TableRow key={t.id} data-testid={`transaction-row-${t.id}`}>
                    <TableCell>{t.date}</TableCell>
                    <TableCell><span className={`text-xs px-2 py-0.5 rounded-full ${b.cls}`} data-testid={`tx-ledger-${t.id}`}>{b.text}</span></TableCell>
                    <TableCell><span className={`text-xs px-2 py-0.5 rounded-full ${t.kind === "incasso" ? "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]" : "bg-destructive/15 text-destructive"}`}>{txLabel(t)}</span></TableCell>
                    <TableCell>{t.category}</TableCell>
                    <TableCell className="text-muted-foreground">{t.description}{t.tax > 0 ? ` · IVA ${fmt(t.tax)}` : ""}</TableCell>
                    <TableCell>{t.kind === "incasso" && ledgerOf(t) === "ufficiale" ? "—" : (t.payment_method || "—")}</TableCell>
                    <TableCell>
                      {t.receipt_path
                        ? <Button size="icon" variant="ghost" onClick={() => viewReceipt(t.receipt_path)} data-testid={`view-receipt-${t.id}`} title="Vedi scontrino"><Receipt className="w-4 h-4 text-primary" /></Button>
                        : <span className="text-muted-foreground text-xs">—</span>}
                    </TableCell>
                    <TableCell className={`text-right font-medium ${t.kind === "incasso" ? "text-[hsl(var(--success))]" : "text-destructive"}`}>{t.kind === "incasso" ? "+" : "−"} {fmt(t.amount)}</TableCell>
                    <TableCell className="whitespace-nowrap"><Button size="icon" variant="ghost" onClick={() => openEditTx(t)} data-testid={`edit-transaction-${t.id}`}><Pencil className="w-4 h-4 text-primary" /></Button><Button size="icon" variant="ghost" onClick={() => delTx(t.id)} data-testid={`delete-transaction-${t.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button></TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="fatture">
          <div className="flex justify-end mb-4">
            <Button onClick={() => { setInvEditId(null); setInvForm(emptyInv); setInvOpen(true); }} className="rounded-full" data-testid="add-invoice-button"><Plus className="w-4 h-4 mr-1" /> Nuova fattura</Button>
          </div>
          <Card className="rounded-xl overflow-hidden">
            <Table data-testid="invoices-table">
              <TableHeader><TableRow><TableHead>Numero</TableHead><TableHead>Data</TableHead><TableHead>Cliente</TableHead><TableHead className="text-right">Imponibile</TableHead><TableHead className="text-right">IVA</TableHead><TableHead className="text-right">Totale</TableHead><TableHead>Stato</TableHead><TableHead></TableHead></TableRow></TableHeader>
              <TableBody>
                {invoices.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">Nessuna fattura</TableCell></TableRow>}
                {invoices.map((v) => (
                  <TableRow key={v.id} data-testid={`invoice-row-${v.id}`}>
                    <TableCell className="font-medium">{v.number}</TableCell>
                    <TableCell>{v.date}</TableCell>
                    <TableCell>{v.client}</TableCell>
                    <TableCell className="text-right">{fmt(v.subtotal)}</TableCell>
                    <TableCell className="text-right">{fmt(v.tax)} <span className="text-xs text-muted-foreground">({v.tax_rate}%)</span></TableCell>
                    <TableCell className="text-right font-medium">{fmt(v.total)}</TableCell>
                    <TableCell><span className="text-xs bg-secondary px-2 py-0.5 rounded-full">{v.status}</span></TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button size="icon" variant="ghost" onClick={() => generateInvoicePdf(v)} data-testid={`pdf-invoice-${v.id}`} title="Scarica PDF"><Download className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => generateInvoicePdf(v, { print: true })} data-testid={`print-invoice-${v.id}`} title="Stampa scontrino"><Printer className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => openEditInv(v)} data-testid={`edit-invoice-${v.id}`} title="Modifica"><Pencil className="w-4 h-4 text-primary" /></Button>
                      <Button size="icon" variant="ghost" onClick={() => delInv(v.id)} data-testid={`delete-invoice-${v.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="debiti">
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-muted-foreground">Debito totale verso fornitori: <span className="font-semibold text-destructive">{fmt(totalDebt)}</span></p>
            <Button onClick={() => { setDebtForm({ name: "", amount: "", note: "" }); setDebtOpen(true); }} className="rounded-full" data-testid="add-debt-button"><Plus className="w-4 h-4 mr-1" /> Aggiungi debito</Button>
          </div>
          <Card className="rounded-xl overflow-hidden">
            <Table data-testid="debts-table">
              <TableHeader><TableRow><TableHead>Fornitore</TableHead><TableHead>Contatti</TableHead><TableHead className="text-right">Debito</TableHead><TableHead className="text-right"></TableHead></TableRow></TableHeader>
              <TableBody>
                {debtors.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">Nessun debito in sospeso 🎉</TableCell></TableRow>}
                {debtors.map((s) => (
                  <TableRow key={s.id} data-testid={`debt-row-${s.id}`}>
                    <TableCell className="font-medium">{s.name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{[s.phone, s.email].filter(Boolean).join(" · ") || "—"}</TableCell>
                    <TableCell className="text-right font-medium text-destructive">{fmt(s.debt)}</TableCell>
                    <TableCell className="text-right"><Button size="sm" variant="outline" className="rounded-full" onClick={() => payDebt(s)} data-testid={`pay-debt-${s.id}`}>Segna come pagata</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="conti"><AccountsPanel /></TabsContent>
        <TabsContent value="chiusura"><CashClosingPanel /></TabsContent>
        <TabsContent value="iva"><VatPanel /></TabsContent>
        <TabsContent value="dasistemare"><PendingDocsPanel onCount={setPendingCount} /></TabsContent>
      </Tabs>

      <Dialog open={txOpen} onOpenChange={(o) => { setTxOpen(o); if (!o) setEditTxId(null); }}>
        <DialogContent data-testid="transaction-dialog">
          <DialogHeader><DialogTitle>{editTxId ? "Modifica movimento" : "Nuovo movimento"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Registro</Label>
              <Select value={txForm.ledger} onValueChange={(v) => setTxForm({ ...txForm, ledger: v, kind: v === "fuori_cassa" ? "spesa" : txForm.kind, category: v === "fuori_cassa" ? "Spese familiari" : txForm.category })}>
                <SelectTrigger data-testid="tx-ledger-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ufficiale">{txForm.kind === "incasso" ? "Ufficiale (Incasso)" : "Ufficiale (con IVA)"}</SelectItem>
                  <SelectItem value="black">{txForm.kind === "incasso" ? "Black (Prelievo)" : "Black / Preventivi"}</SelectItem>
                  {txForm.kind === "spesa" && <SelectItem value="fuori_cassa">Fuori cassa (spese familiari)</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Tipo</Label>
                <Select value={txForm.kind} onValueChange={(v) => setTxForm({ ...txForm, kind: v, tax_rate: v === "incasso" ? 10 : 22 })} disabled={txForm.ledger === "fuori_cassa"}>
                  <SelectTrigger data-testid="tx-kind-select"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="incasso">Incasso</SelectItem><SelectItem value="spesa">Spesa</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>{showIva ? "Importo lordo (€)" : "Importo (€)"}</Label><Input type="number" step="0.01" value={txForm.amount} onChange={(e) => setTxForm({ ...txForm, amount: e.target.value })} data-testid="tx-amount-input" /></div>
            </div>
            {showIva && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
                <div className="space-y-2"><Label>IVA (%)</Label><Input type="number" value={txForm.tax_rate} onChange={(e) => setTxForm({ ...txForm, tax_rate: e.target.value })} data-testid="tx-taxrate-input" /></div>
                <p className="text-sm text-muted-foreground pb-2" data-testid="tx-iva-preview">Imponibile {fmt(imponibilePrev)} · IVA {fmt(grossPrev - imponibilePrev)}</p>
              </div>
            )}
            <div className="space-y-2"><Label>Categoria</Label>
              {txForm.kind === "spesa" ? (
                <Select value={EXPENSE_CATS.includes(txForm.category) ? txForm.category : ""} onValueChange={(v) => setTxForm({ ...txForm, category: v, ledger: v === "Spese familiari" ? "fuori_cassa" : (txForm.ledger === "fuori_cassa" ? "ufficiale" : txForm.ledger) })}>
                  <SelectTrigger data-testid="tx-category-select"><SelectValue placeholder="Scegli una categoria" /></SelectTrigger>
                  <SelectContent>{EXPENSE_CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <Select value={INCOME_CATS.includes(txForm.category) ? txForm.category : ""} onValueChange={(v) => setTxForm({ ...txForm, category: v, payment_method: INCOME_METHOD[v] || txForm.payment_method })}>
                  <SelectTrigger data-testid="tx-category-select"><SelectValue placeholder="Provenienza incasso" /></SelectTrigger>
                  <SelectContent>{INCOME_CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Data</Label><Input type="date" value={txForm.date} onChange={(e) => setTxForm({ ...txForm, date: e.target.value })} data-testid="tx-date-input" /></div>
              {txForm.kind === "incasso" && txForm.ledger === "ufficiale" ? (
                <div className="space-y-2"><Label>Metodo</Label>
                  <div className="h-10 flex items-center px-3 rounded-md border border-border/60 bg-muted/40 text-sm text-muted-foreground" data-testid="tx-method-none">Nessun conto · solo Contabilità Ufficiale</div>
                </div>
              ) : (
                <div className="space-y-2"><Label>Metodo</Label>
                  <Select value={txForm.payment_method} onValueChange={(v) => setTxForm({ ...txForm, payment_method: v })}>
                    <SelectTrigger data-testid="tx-method-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="contante">Contanti</SelectItem><SelectItem value="sumup">SumUp</SelectItem><SelectItem value="banca">Banca</SelectItem><SelectItem value="deposito">Deposito</SelectItem><SelectItem value="fornitore">Sospeso (debito fornitore)</SelectItem></SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="space-y-2"><Label>Descrizione</Label><Input value={txForm.description} onChange={(e) => setTxForm({ ...txForm, description: e.target.value })} data-testid="tx-description-input" /></div>
            <div className="space-y-2">
              <Label>Foto o PDF scontrino (opzionale)</Label>
              <input ref={txFileRef} type="file" accept="image/*,application/pdf" onChange={(e) => onTxFile(e.target.files?.[0] || null)} className="hidden" data-testid="tx-file-input" />
              <Button type="button" variant="outline" className="w-full rounded-xl" onClick={() => txFileRef.current?.click()} disabled={txReading} data-testid="tx-photo-button">
                {txReading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Lettura AI…</> : <><Camera className="w-4 h-4 mr-2" /> {txFile ? txFile.name.slice(0, 28) : "Allega foto o PDF (AI)"}</>}
              </Button>
            </div>
          </div>
          <DialogFooter><Button onClick={saveTx} className="rounded-full" data-testid="save-transaction-button">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={invOpen} onOpenChange={(o) => { setInvOpen(o); if (!o) setInvEditId(null); }}>
        <DialogContent data-testid="invoice-dialog">
          <DialogHeader><DialogTitle>{invEditId ? "Modifica fattura" : "Nuova fattura"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Numero</Label><Input value={invForm.number} onChange={(e) => setInvForm({ ...invForm, number: e.target.value })} placeholder="2026/001" data-testid="inv-number-input" /></div>
              <div className="space-y-2"><Label>Data</Label><Input type="date" value={invForm.date} onChange={(e) => setInvForm({ ...invForm, date: e.target.value })} data-testid="inv-date-input" /></div>
            </div>
            <div className="space-y-2"><Label>Cliente</Label><Input value={invForm.client} onChange={(e) => setInvForm({ ...invForm, client: e.target.value })} data-testid="inv-client-input" /></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Imponibile (€)</Label><Input type="number" step="0.01" value={invForm.subtotal} onChange={(e) => setInvForm({ ...invForm, subtotal: e.target.value })} data-testid="inv-subtotal-input" /></div>
              <div className="space-y-2"><Label>IVA (%)</Label><Input type="number" value={invForm.tax_rate} onChange={(e) => setInvForm({ ...invForm, tax_rate: e.target.value })} data-testid="inv-tax-input" /></div>
            </div>
            {invForm.subtotal && (
              <p className="text-sm text-muted-foreground">Totale con IVA: <span className="font-medium text-foreground">{fmt(parseFloat(invForm.subtotal) * (1 + parseFloat(invForm.tax_rate) / 100))}</span></p>
            )}
          </div>
          <DialogFooter><Button onClick={saveInv} className="rounded-full" data-testid="save-invoice-button">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={debtOpen} onOpenChange={setDebtOpen}>
        <DialogContent data-testid="debt-dialog">
          <DialogHeader><DialogTitle>Aggiungi debito pregresso</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Fornitore</Label><Input value={debtForm.name} onChange={(e) => setDebtForm({ ...debtForm, name: e.target.value })} placeholder="Nome fornitore" data-testid="debt-name-input" /></div>
            <div className="space-y-2"><Label>Importo debito (€)</Label><Input type="number" step="0.01" value={debtForm.amount} onChange={(e) => setDebtForm({ ...debtForm, amount: e.target.value })} data-testid="debt-amount-input" /></div>
            <div className="space-y-2"><Label>Nota (opzionale)</Label><Input value={debtForm.note} onChange={(e) => setDebtForm({ ...debtForm, note: e.target.value })} data-testid="debt-note-input" /></div>
          </div>
          <DialogFooter><Button onClick={addDebt} className="rounded-full" data-testid="save-debt-button">Salva debito</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent data-testid="pay-debt-dialog">
          <DialogHeader><DialogTitle>Segna come pagata · {paySup?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Debito attuale: <span className="font-semibold text-destructive">{fmt(paySup?.debt)}</span></p>
            <div className="space-y-2"><Label>Importo da saldare (€)</Label><Input type="number" step="0.01" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} data-testid="pay-amount-input" /></div>
            <div className="space-y-2"><Label>Metodo di pagamento (esce da)</Label>
              <Select value={payForm.payment_method} onValueChange={(v) => setPayForm({ ...payForm, payment_method: v })}>
                <SelectTrigger data-testid="pay-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>{WALLETS.map((w) => <SelectItem key={w.v} value={w.v}>{w.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button onClick={doPay} className="rounded-full" data-testid="confirm-pay-button">Registra pagamento</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
